import chartSquareBar from '@iconify/icons-heroicons-outline/chart-square-bar';
import clock from '@iconify/icons-heroicons-outline/clock';
import documentDuplicate from '@iconify/icons-heroicons-outline/document-duplicate';
import newspaper from '@iconify/icons-heroicons-outline/newspaper';
import officeBuilding from '@iconify/icons-heroicons-outline/office-building';
import questionMarkCircle from '@iconify/icons-heroicons-outline/question-mark-circle';
import trendingUp from '@iconify/icons-heroicons-outline/trending-up';
import userGroup from '@iconify/icons-heroicons-outline/user-group';
import { useMemo } from 'react';

import { RolesWithProduct } from '@customTypes/api/Commons';
import SidebarConfig from '@customTypes/SidebarConfig';
import useFeatureFlags from '@hooks/useFeatureFlags';
import pathnames from '@src/app/routes/pathnames';
import { useAuthorizationContext } from '@src/context/AuthorizationContext';
import { Roles } from '@src/context/AuthorizationContext/types';

import useGetAgentDocumentUnsigned from './reactQueryHooks/AgentManagement/useGetAgentDocumentUnsigned';

const useSidebarConfig = () => {
  const { features, routes } = useFeatureFlags();

  const { isAllowed, rolesProduct } = useAuthorizationContext();

  const {
    features: { agentDocumentUnsigned },
  } = useFeatureFlags();

  const { data: notificationData } = useGetAgentDocumentUnsigned({
    enabled:
      agentDocumentUnsigned() &&
      [
        RolesWithProduct['SPV.2W'],
        RolesWithProduct['SPV.4W'],
        RolesWithProduct['SPV.PBF'],
      ].includes(rolesProduct),
  });

  const notifications = useMemo(
    () => ({
      agentManagement: !!notificationData,
    }),
    [notificationData],
  );

  const AVAILABILITY_MAP = useMemo(() => {
    const isPBF = rolesProduct === RolesWithProduct['SPV.PBF'];

    return {
      PERFORMANCE: {
        AREA_DASHBOARD: isAllowed('area-dashboard'),
        BRANCH_DASHBOARD: isAllowed('branch-dashboard'),
        BRANCH_PERFORMANCE: isAllowed('branch-performance'),
        SPV_DASHBOARD: isAllowed('spv-dashboard'),
        SPV_PERFORMANCE: isAllowed('spv-performance'),
        ARE_PERFORMANCE: isAllowed('are-performance') && !isPBF,
        AGENT_PERFORMANCE: isAllowed('agent-performance.this-month') && !isPBF,
        KPI_INCENTIVE:
          isAllowed('kpi-incentive') && (!isPBF || routes.kpiIncentiveForPBF()),
      },

      BRANCH_ACTIVITY: {
        WEEKLY_PLANNING: {
          RECRUITMENT:
            isAllowed('recruitment.recruitment-area') ||
            isAllowed('recruitment.recruitment-list'),
          MAINTAIN:
            isAllowed('maintain.assignment') ||
            isAllowed('maintain.are-schedule'),
          MAINTAIN_NON_REGIST:
            isAllowed('maintain-non-regist.assignment') ||
            isAllowed('maintain-non-regist.schedule'),
        },
        JOIN_VISIT:
          isAllowed('join-visit.this-month') ||
          isAllowed('join-visit.historical'),
        '1ON1_SPV_ARE':
          (isAllowed('1-on-1.sales') ||
            isAllowed('1-on-1.maintain') ||
            isAllowed('1-on-1.recruit')) &&
          (!isPBF || routes['1on1ForPBF']()),
        '1ON1_BM_SPV':
          isAllowed('1-on-1-bm.sales') ||
          isAllowed('1-on-1-bm.maintain') ||
          isAllowed('1-on-1-bm.recruit'),
        WEEKLY_MEETING: isAllowed('weekly-meeting') && !isPBF,
        BRANCH_ROUTINE_CHECKLIST:
          isAllowed('branch-routine-checklist') &&
          features.branchRoutineChecklist(),
      },

      REPORT: {
        MAINTAIN: isAllowed('report.maintain'),
        RECRUIT: isAllowed('report.recruit'),
      },

      TEAM_MANAGEMENT: {
        AGENT_MANAGEMENT:
          (isAllowed('agent-management') ||
            isAllowed('agent-management.blacklist')) &&
          (!isPBF || routes.agentManagementForPBF()),
        ARE_HIERARCHY:
          isAllowed('hierarchy') && (!isPBF || routes.hierarchyForPBF()),
      },

      LEADS_TRACKER: isAllowed('leads-tracker') && !isPBF,

      AGENCY_NEWS: {
        UPDATES: isAllowed('agency-news.updates') && !isPBF,
        PROMO_MATERIAL: isAllowed('agency-news.promo-material') && !isPBF,
        LOYALTY_PROGRAM: isAllowed('agency-news.loyalty-program') && !isPBF,
        TRAINING_MATERIAL: isAllowed('agency-news.training-material') && !isPBF,
      },

      SETTING_AGENT_EXPIRY: isAllowed('setting-agent-expiry'),

      USER_GUIDE: true,
    };
  }, [features, isAllowed, rolesProduct, routes]);

  const configs: SidebarConfig[] = useMemo(
    () => [
      {
        label: 'sidebar.performanceDashboard',
        isAvailable: Object.values(AVAILABILITY_MAP.PERFORMANCE).some(Boolean),
        icon: chartSquareBar,
        children: [
          {
            label: 'sidebar.areaDashboard',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.AREA_DASHBOARD,
            path: pathnames.home(),
          },
          {
            label: 'sidebar.branchDashboard',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.BRANCH_DASHBOARD,
            path: pathnames.home(),
          },
          {
            label: 'sidebar.branchPerformance',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.BRANCH_PERFORMANCE,
            path: pathnames.branchPerformance(),
          },
          {
            label: 'sidebar.spvDashboard',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.SPV_DASHBOARD,
            path: rolesProduct.includes(Roles.Spv)
              ? pathnames.home()
              : pathnames.spvPerformance(),
          },
          {
            label: 'sidebar.spvPerformance',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.SPV_PERFORMANCE,
            path: pathnames.spvPerformance(),
          },
          {
            label: 'sidebar.arePerformance',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.ARE_PERFORMANCE,
            path: pathnames.arePerformance(),
          },
          {
            label: 'sidebar.agentPerformance',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.AGENT_PERFORMANCE,
            path: pathnames.agentPerformance(),
          },
          {
            label: 'sidebar.kpiIncentive',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.KPI_INCENTIVE,
            path: pathnames.kpiIncentive(),
          },
        ],
      },
      {
        label: 'sidebar.branchActivity',
        isAvailable:
          AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.RECRUITMENT ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.MAINTAIN ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY.JOIN_VISIT ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SPV_ARE'] ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_BM_SPV'] ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_MEETING ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY.BRANCH_ROUTINE_CHECKLIST,
        icon: officeBuilding,
        children: [
          {
            label: 'sidebar.weeklyPlanning',
            isAvailable:
              AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.RECRUITMENT ||
              AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.MAINTAIN,
            children: [
              {
                label: 'sidebar.recruitment',
                isAvailable:
                  AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.RECRUITMENT,
                path: pathnames.recruitActivity(),
              },
              {
                label: 'sidebar.maintain',
                isAvailable:
                  AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.MAINTAIN,
                path: pathnames.maintainActivity(),
              },
              {
                label: 'sidebar.maintainNonRegist',
                isAvailable:
                  AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING
                    .MAINTAIN_NON_REGIST,
                path: pathnames.maintainNonRegistActivity(),
              },
            ],
          },
          {
            label: 'sidebar.weeklyMeeting',
            isAvailable: AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_MEETING,
            path: pathnames.weeklyMeeting(),
          },
          {
            label: 'sidebar.joinVisit',
            isAvailable: AVAILABILITY_MAP.BRANCH_ACTIVITY.JOIN_VISIT,
            path: pathnames.joinVisit(),
          },
          {
            label: 'sidebar.spvDownline1on1',
            isAvailable: AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SPV_ARE'],
            path: pathnames.oneOnOne(),
          },
          {
            label: 'sidebar.bmSpv1on1',
            isAvailable: AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_BM_SPV'],
            path: pathnames.oneOnOne(),
          },
          {
            label: 'sidebar.branchRoutineChecklist',
            isAvailable:
              AVAILABILITY_MAP.BRANCH_ACTIVITY.BRANCH_ROUTINE_CHECKLIST,
            path: pathnames.branchRoutineChecklist(),
          },
        ],
      },
      {
        label: 'sidebar.report',
        isAvailable:
          AVAILABILITY_MAP.REPORT.MAINTAIN || AVAILABILITY_MAP.REPORT.RECRUIT,
        icon: documentDuplicate,
        children: [
          {
            label: 'sidebar.maintainReport',
            isAvailable: AVAILABILITY_MAP.REPORT.MAINTAIN,
            path: pathnames.maintainReport(),
          },
          {
            label: 'sidebar.recruitReport',
            isAvailable: AVAILABILITY_MAP.REPORT.RECRUIT,
            path: pathnames.recruitReport(),
          },
        ],
      },
      {
        label: 'sidebar.teamManagement',
        isAvailable:
          AVAILABILITY_MAP.TEAM_MANAGEMENT.AGENT_MANAGEMENT ??
          AVAILABILITY_MAP.TEAM_MANAGEMENT.ARE_HIERARCHY,
        icon: userGroup,
        hasNotification: notifications.agentManagement,
        children: [
          {
            label: 'sidebar.agentManagement',
            isAvailable: AVAILABILITY_MAP.TEAM_MANAGEMENT.AGENT_MANAGEMENT,
            path: pathnames.agentManagement(),
            hasNotification: notifications.agentManagement,
          },
          {
            label: routes.hierarchyForPBF()
              ? 'sidebar.settingDownline'
              : 'sidebar.areHierarchy',
            isAvailable: AVAILABILITY_MAP.TEAM_MANAGEMENT.ARE_HIERARCHY,
            path: pathnames.teamHierarchy(),
          },
        ],
      },
      {
        label: 'sidebar.leadsTracker',
        isAvailable: AVAILABILITY_MAP.LEADS_TRACKER,
        icon: trendingUp,
        path: pathnames.leadsTracker(),
      },
      {
        label: 'sidebar.agencyNews',
        isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.UPDATES,
        icon: newspaper,
        children: [
          {
            label: 'sidebar.updates',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.UPDATES,
            path: pathnames.agencyNewsUpdates(),
          },
          {
            label: 'sidebar.promoMaterial',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.PROMO_MATERIAL,
            path: pathnames.agencyNewsPromoMaterial(),
          },
          {
            label: 'sidebar.loyaltyProgram',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.LOYALTY_PROGRAM,
            path: pathnames.agencyNewsLoyaltyProgram(),
          },
          {
            label: 'sidebar.trainingMaterial',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.TRAINING_MATERIAL,
            path: pathnames.agencyNewsTrainingMaterial(),
          },
        ],
      },
      {
        label: 'sidebar.settingAgentExpiry',
        isAvailable: AVAILABILITY_MAP.SETTING_AGENT_EXPIRY,
        icon: clock,
        path: pathnames.settingAgentExpiry(),
      },
      {
        label: 'sidebar.userGuide',
        isAvailable: AVAILABILITY_MAP.USER_GUIDE,
        icon: questionMarkCircle,
        path: pathnames.userGuide(),
      },
    ],
    [
      AVAILABILITY_MAP.PERFORMANCE,
      AVAILABILITY_MAP.BRANCH_ACTIVITY,
      AVAILABILITY_MAP.REPORT.MAINTAIN,
      AVAILABILITY_MAP.REPORT.RECRUIT,
      AVAILABILITY_MAP.TEAM_MANAGEMENT.AGENT_MANAGEMENT,
      AVAILABILITY_MAP.TEAM_MANAGEMENT.ARE_HIERARCHY,
      AVAILABILITY_MAP.LEADS_TRACKER,
      AVAILABILITY_MAP.AGENCY_NEWS.UPDATES,
      AVAILABILITY_MAP.AGENCY_NEWS.PROMO_MATERIAL,
      AVAILABILITY_MAP.AGENCY_NEWS.LOYALTY_PROGRAM,
      AVAILABILITY_MAP.AGENCY_NEWS.TRAINING_MATERIAL,
      AVAILABILITY_MAP.SETTING_AGENT_EXPIRY,
      AVAILABILITY_MAP.USER_GUIDE,
      rolesProduct,
      notifications.agentManagement,
      routes,
    ],
  );

  return {
    configs,
    hasNotification: Object.values(notifications).some(
      notificationValue => !!notificationValue,
    ),
  };
};

export default useSidebarConfig;
